import Padding from '@/components/padding/Padding';
import { trackContactFormSubmit, trackWhatsappClick } from '@/utils/analytics';

import useContactForm from '@/pages/contactUs/hooks/useContactForm';
import {
  Box,
  Button,
  Field,
  Heading,
  Icon,
  Input,
  Spinner,
  Text,
  Textarea,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { FaCheckCircle, FaExclamationTriangle, FaInstagram, FaWhatsapp } from 'react-icons/fa';

const ContactUsForm = (): React.JSX.Element => {
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState(false);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);

  const [phone, setPhone] = useState('');

  const [message, setMessage] = useState('');
  const [messageError, setMessageError] = useState(false);

  const { submitContactForm, isLoading, sent, error } = useContactForm();

  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined;
  const whatsappNumber = import.meta.env.VITE_WHATSAPP_NUMBER as string | undefined;

  type Grecaptcha = {
    ready: (cb: () => void) => void;
    execute: (siteKey: string, options: { action: string }) => Promise<string>;
  };

  const loadRecaptcha = async (): Promise<Grecaptcha> => {
    if (typeof window === 'undefined') {
      throw new Error('Window is not available');
    }

    const w = window as unknown as { grecaptcha?: Grecaptcha };

    // If already available, resolve when it's ready
    if (w.grecaptcha && typeof w.grecaptcha.execute === 'function') {
      await new Promise<void>((resolve) => w.grecaptcha!.ready(() => resolve()));
      return w.grecaptcha!;
    }

    if (!siteKey) {
      throw new Error('Missing VITE_RECAPTCHA_SITE_KEY');
    }

    // Inject script if not present
    let script = document.getElementById('recaptcha-v3') as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = 'recaptcha-v3';
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    await new Promise<void>((resolve, reject) => {
      const onLoad = () => {
        const g = (window as unknown as { grecaptcha?: Grecaptcha }).grecaptcha;
        if (!g) {
          reject(new Error('grecaptcha unavailable after script load'));
          return;
        }
        g.ready(() => resolve());
      };
      script!.addEventListener('load', onLoad, { once: true });
      script!.addEventListener(
        'error',
        () => reject(new Error('Failed to load reCAPTCHA script')),
        {
          once: true,
        },
      );
      // If it was already loaded before listeners attached
      const g = (window as unknown as { grecaptcha?: Grecaptcha }).grecaptcha;
      if (g?.ready) {
        g.ready(() => resolve());
      }
    });

    return (window as unknown as { grecaptcha?: Grecaptcha }).grecaptcha as Grecaptcha;
  };

  useEffect(() => {
    // Preload recaptcha in the background
    loadRecaptcha().catch(() => {
      // silently ignore here; will show error on submit if needed
    });
    // no cleanup needed; script can remain cached
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track conversion when form is successfully submitted
  useEffect(() => {
    if (sent) {
      trackContactFormSubmit();
    }
  }, [sent]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    if (!name) {
      setNameError(true);
    } else {
      setNameError(false);
    }

    if (!email) {
      setEmailError(true);
    } else {
      setEmailError(false);
    }

    if (!message) {
      setMessageError(true);
    } else {
      setMessageError(false);
    }

    // If any validation error, do not proceed
    if (!name || !email || !message) {
      return;
    }

    const submitWithCaptcha = async () => {
      try {
        const grecaptcha = await loadRecaptcha();
        const token = await grecaptcha.execute(siteKey!, { action: 'contact_form' });
        await submitContactForm(name, email, phone, message, token);
      } catch (err: unknown) {
        console.error(err);
      }
    };

    void submitWithCaptcha();
  };

  return (
    <Padding verticalPadding={20}>
      <Box className="mx-auto max-w-6xl px-4">
        <Box className="rounded-xl md:p-8 lg:p-16 md:shadow-2xl md:p-10 flex flex-col md:flex-row gap-8">
          {/* Left hand side */}
          <Box className="flex-1">
            <Heading mt={4} fontSize="3xl" style={{ fontFamily: 'Bebas Neue' }}>
              Get In Touch With Us
            </Heading>
            <Text fontSize="md" style={{ fontFamily: 'Lato' }} className="mt-2">
              If you have a question, need support, or just want to learn more about Olympiq, we’re
              here to help. Send us a message and we’ll aim to get back to you within one working
              day. And if you decide to use Olympiq, you’ll be given the direct mobile number of a
              dedicated support team member - so you can always speak to the same person,
              personally, whenever you need help.
            </Text>
            <a
              href={whatsappNumber ? `https://wa.me/${whatsappNumber}` : undefined}
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-6"
              onClick={() => trackWhatsappClick()}
            >
              <Box className="flex items-start gap-4 items-center bg-[#f9f9f9] dark:bg-[#16161f] p-4 md:p-2 rounded-md hover:opacity-80 transition-opacity">
                <Box className="flex h-12 w-12 items-center justify-center rounded-md bg-[#25D366] text-white">
                  <Icon as={FaWhatsapp} boxSize={7} />
                </Box>
                <Box>
                  <Text fontSize="md" fontWeight="bold" style={{ fontFamily: 'Lato' }}>
                    Chat with us on WhatsApp
                  </Text>
                  <Text fontSize="md" style={{ fontFamily: 'Lato' }} className="underline">
                    Message us now
                  </Text>
                </Box>
              </Box>
            </a>
            <a
              href="https://www.instagram.com/olympiq_app/"
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-4"
            >
              <Box className="flex items-start gap-4 items-center bg-[#f9f9f9] dark:bg-[#16161f] p-4 md:p-2 rounded-md hover:opacity-80 transition-opacity">
                <Box
                  className="flex h-12 w-12 items-center justify-center rounded-md text-white"
                  style={{
                    background:
                      'radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)',
                  }}
                >
                  <Icon as={FaInstagram} boxSize={7} />
                </Box>
                <Box>
                  <Text fontSize="md" fontWeight="bold" style={{ fontFamily: 'Lato' }}>
                    Follow us on Instagram
                  </Text>
                  <Text fontSize="md" style={{ fontFamily: 'Lato' }} className="underline">
                    @olympiq_app
                  </Text>
                </Box>
              </Box>
            </a>
          </Box>

          {/* Right hand side */}
          <Box className="flex-1">
            <Box className="rounded-md border border-[#16161f] dark:border-white p-4">
              <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
                <Field.Root invalid={nameError}>
                  <Field.Label>Name</Field.Label>
                  <Input
                    type="text"
                    placeholder="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  <Field.ErrorText>Name is required</Field.ErrorText>
                </Field.Root>
                <Field.Root invalid={emailError}>
                  <Field.Label>Email address</Field.Label>
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <Field.ErrorText>Email address is required</Field.ErrorText>
                </Field.Root>
                <Field.Root>
                  <Field.Label>Phone number</Field.Label>
                  <Input
                    type="tel"
                    placeholder="Phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                  <Field.HelperText>Optional</Field.HelperText>
                </Field.Root>
                <Field.Root invalid={messageError}>
                  <Field.Label>Message</Field.Label>
                  <Textarea
                    placeholder="Message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                  <Field.ErrorText>A message is required</Field.ErrorText>
                </Field.Root>
                <Button mt={4} type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <Box display="inline-flex" alignItems="center" gap="2">
                      <Spinner size="sm" />
                      <span>Submitting</span>
                    </Box>
                  ) : (
                    'Submit'
                  )}
                </Button>

                {error && (
                  <Box
                    p={4}
                    bgColor="red.100"
                    rounded="sm"
                    className="flex justify-center items-center gap-4"
                  >
                    <Icon as={FaExclamationTriangle} color="red.500" />
                    <Text color="red.700" fontSize="sm">
                      Something went wrong. Please try again.
                    </Text>
                  </Box>
                )}
                {sent && (
                  <Box
                    p={4}
                    bgColor="green.100"
                    rounded="sm"
                    className="flex justify-center items-center gap-4"
                  >
                    <Icon as={FaCheckCircle} color="green.500" />
                    <Text color="green.700" fontSize="sm">
                      Message sent successfully.
                    </Text>
                  </Box>
                )}
              </form>
            </Box>
          </Box>
        </Box>
      </Box>
    </Padding>
  );
};

export default ContactUsForm;
